import { METHOD_CONFIG, type MethodInput, type RegimenColumn } from '../constants/methodConfig';
import type { AdministrationMode } from '../types';
import { normalizeUnitCode, unitConversionFactor } from './quantityUnits';

const ADMINISTRATION_MODES: AdministrationMode[] = ['intermittent', 'continuous', 'oral', 'bolus'];

export const normalizeAdministrationMode = (
  value: unknown,
  fallback: AdministrationMode = 'intermittent',
): AdministrationMode => {
  const normalized = String(value ?? '').trim().toLowerCase();
  return ADMINISTRATION_MODES.includes(normalized as AdministrationMode)
    ? normalized as AdministrationMode
    : fallback;
};

export const getSupportedAdministrationModes = (drugConfig: any): AdministrationMode[] => {
  const rawModes: unknown[] = Array.isArray(drugConfig?.supported_administration_modes)
    ? drugConfig.supported_administration_modes
    : [];
  const modes = rawModes.reduce<AdministrationMode[]>((acc, mode) => {
    const normalized = normalizeAdministrationMode(mode, '' as AdministrationMode);
    if (normalized && ADMINISTRATION_MODES.includes(normalized) && !acc.includes(normalized)) {
      acc.push(normalized);
    }
    return acc;
  }, []);
  return modes.length > 0 ? modes : ['intermittent'];
};

export const getDefaultAdministrationMode = (drugConfig: any): AdministrationMode => {
  const supportedModes = getSupportedAdministrationModes(drugConfig);
  const requested = normalizeAdministrationMode(drugConfig?.default_administration_mode, supportedModes[0]);
  return supportedModes.includes(requested) ? requested : supportedModes[0];
};

export const getAdministrationModeLabel = (mode: AdministrationMode): string => {
  if (mode === 'continuous') return '连续输注';
  if (mode === 'oral') return '口服';
  if (mode === 'bolus') return '静脉注射';
  return '静脉滴注';
};

export const getPreferredTargetParameter = (
  method: string,
  administrationMode: AdministrationMode,
  continuousTargetParameter = 'Css',
): string | undefined => {
  if (method === 'Target Concentration') {
    return administrationMode === 'continuous' ? continuousTargetParameter : 'Cmin';
  }
  if (method === 'Target Cmax') return 'Cmax';
  if (method === 'Target AUC') return 'AUC24';
  if (method === 'AUC/MIC') return 'AUC24/MIC';
  if (method === '%fT>MIC') return '%fT>MIC';
  if (method === 'Cmax/MIC') return 'Cmax/MIC';
  return undefined;
};

export const findTargetForAdministrationMode = (
  drugConfig: any,
  method: string,
  administrationMode: AdministrationMode,
  continuousTargetParameter = 'Css',
  parameter = getPreferredTargetParameter(method, administrationMode, continuousTargetParameter),
): any | undefined => {
  const targets = Array.isArray(drugConfig?.targets) ? drugConfig.targets : [];
  const matchesMode = (target: any) => {
    const modes = target?.administration_modes;
    return !Array.isArray(modes) || modes.length === 0 || modes.includes(administrationMode);
  };
  const scopedMatch = targets.find((target: any) => target?.method === method && matchesMode(target))
    || (parameter && targets.find((target: any) => target?.parameter === parameter && matchesMode(target)));
  if (scopedMatch) return scopedMatch;

  const hasModeScopedTarget = targets.some((target: any) => {
    if (!Array.isArray(target?.administration_modes) || target.administration_modes.length === 0) return false;
    return target.method === method || (parameter && target.parameter === parameter);
  });
  if (hasModeScopedTarget) return undefined;

  return targets.find((target: any) => target?.method === method)
    || (parameter && targets.find((target: any) => target?.parameter === parameter));
};

export const getTargetForMethodInput = (
  drugConfig: any,
  inputName: string,
  method: string,
  administrationMode: AdministrationMode,
  continuousTargetParameter = 'Css',
): any | undefined => {
  if (inputName === 'target_auc') {
    return findTargetForAdministrationMode(drugConfig, 'Target AUC', administrationMode, continuousTargetParameter, 'AUC24');
  }
  if (inputName === 'target_auc_mic') {
    return findTargetForAdministrationMode(drugConfig, 'AUC/MIC', administrationMode, continuousTargetParameter, 'AUC24/MIC');
  }
  if (inputName === 'target_ft') {
    return findTargetForAdministrationMode(drugConfig, '%fT>MIC', administrationMode, continuousTargetParameter, '%fT>MIC');
  }
  if (inputName === 'target_cmin') {
    return findTargetForAdministrationMode(
      drugConfig,
      'Target Concentration',
      administrationMode,
      continuousTargetParameter,
      getPreferredTargetParameter('Target Concentration', administrationMode, continuousTargetParameter),
    );
  }
  if (inputName === 'target_cmax') {
    return findTargetForAdministrationMode(drugConfig, 'Target Cmax', administrationMode, continuousTargetParameter, 'Cmax');
  }
  if (method) {
    return findTargetForAdministrationMode(drugConfig, method, administrationMode, continuousTargetParameter);
  }
  return undefined;
};

export const getConfiguredInputUnit = (
  drugConfig: any,
  input: MethodInput,
  method: string,
  administrationMode: AdministrationMode,
  continuousTargetParameter = 'Css',
): string => {
  if (input.name === 'mic') {
    return String(
      drugConfig?.units?.mic?.display_unit
      || drugConfig?.mic_unit
      || input.unit
      || '',
    );
  }
  const target = getTargetForMethodInput(
    drugConfig,
    input.name,
    method,
    administrationMode,
    continuousTargetParameter,
  );
  return String(target?.unit || input.unit || '');
};

export const getConcentrationDisplayUnit = (
  drugConfig: any,
  method: string,
  administrationMode: AdministrationMode,
  continuousTargetParameter = 'Css',
  fallback = 'mg/L',
): string => {
  const primaryMethod = method === 'Target Cmax' || method === 'Cmax/MIC'
    ? 'Target Cmax'
    : 'Target Concentration';
  const target = findTargetForAdministrationMode(
    drugConfig,
    primaryMethod,
    administrationMode,
    continuousTargetParameter,
  ) || findTargetForAdministrationMode(
    drugConfig,
    'Target Concentration',
    administrationMode,
    continuousTargetParameter,
  ) || findTargetForAdministrationMode(
    drugConfig,
    'Target Cmax',
    administrationMode,
    continuousTargetParameter,
  );
  return String(
    target?.unit
    || drugConfig?.units?.concentration?.display_unit
    || drugConfig?.concentration_unit
    || fallback,
  );
};

const normalizeConcentrationUnit = (unit: unknown): string => (
  String(unit || '')
    .trim()
    .replace(/μ/g, 'u')
    .replace(/µ/g, 'u')
    .replace(/\s+/g, '')
    .toLowerCase()
);

export const concentrationFactorPerMgL = (unit: unknown): number => {
  const registeredFactor = unitConversionFactor(
    'mg/L',
    normalizeUnitCode(unit || 'mg/L'),
    'concentration',
  );
  if (Number.isFinite(registeredFactor)) return registeredFactor;
  const normalized = normalizeConcentrationUnit(unit);
  if (!normalized) return 1;
  if (['mg/l', 'mgperliter', 'mg*l^-1', 'mg·l-1', 'mg路l-1'].includes(normalized)) return 1;
  if (['ug/ml', 'mcg/ml'].includes(normalized)) return 1;
  if (['ng/ml', 'ug/l', 'mcg/l'].includes(normalized)) return 1000;
  if (normalized === 'mg/ml') return 0.001;
  return 1;
};

export const getConcentrationDisplayScale = (
  drugConfig: any,
  method: string,
  administrationMode: AdministrationMode,
  continuousTargetParameter = 'Css',
): number => {
  const primaryMethod = method === 'Target Cmax' || method === 'Cmax/MIC'
    ? 'Target Cmax'
    : 'Target Concentration';
  const target = findTargetForAdministrationMode(
    drugConfig,
    primaryMethod,
    administrationMode,
    continuousTargetParameter,
  ) || findTargetForAdministrationMode(
    drugConfig,
    'Target Concentration',
    administrationMode,
    continuousTargetParameter,
  ) || findTargetForAdministrationMode(
    drugConfig,
    'Target Cmax',
    administrationMode,
    continuousTargetParameter,
  ) || {};

  const displayUnit = target?.unit
    || drugConfig?.units?.concentration?.display_unit
    || drugConfig?.concentration_unit
    || 'mg/L';
  return unitConversionFactor('mg/L', displayUnit, 'concentration');
};

const CONCENTRATION_DATA_KEYS = [
  'predicted_cmin',
  'predicted_cmax',
  'predicted_css',
  'css_trough',
  'css_peak',
  'free_css_trough',
  'free_css_peak',
];

const isConcentrationColumn = (column: RegimenColumn): boolean => (
  CONCENTRATION_DATA_KEYS.some((key) => column.dataKey.includes(key))
  || /Cmin|Cmax|Css|峰浓度|谷浓度/.test(column.title)
);

export const replaceConcentrationUnitInTitle = (title: string, unit: string): string => {
  if (!unit) return title;
  if (/\([^)]*(?:mg\/L|ng\/mL|ng\/ml|ug\/L|ug\/ml|mcg\/L|mcg\/ml|µg\/L|µg\/ml|μg\/L|μg\/ml)[^)]*\)/i.test(title)) {
    return title.replace(
      /\([^)]*(?:mg\/L|ng\/mL|ng\/ml|ug\/L|ug\/ml|mcg\/L|mcg\/ml|µg\/L|µg\/ml|μg\/L|μg\/ml)[^)]*\)/i,
      `(${unit})`,
    );
  }
  return title;
};

export const applyConfiguredConcentrationUnits = (
  columns: RegimenColumn[],
  drugConfig: any,
  method: string,
  administrationMode: AdministrationMode,
  continuousTargetParameter = 'Css',
): RegimenColumn[] => {
  const unit = getConcentrationDisplayUnit(
    drugConfig,
    method,
    administrationMode,
    continuousTargetParameter,
  );
  return columns.map((column) => (
    isConcentrationColumn(column)
      ? { ...column, title: replaceConcentrationUnitInTitle(column.title, unit) }
      : column
  ));
};

export const resolveAvailableCalculationMethods = (
  drugConfig: any,
  administrationMode: AdministrationMode,
  continuousTargetParameter = 'Css',
): string[] => {
  const methods = Array.isArray(drugConfig?.calculation_methods) ? drugConfig.calculation_methods : [];
  return methods.filter((method: string) => (
    method !== 'Remedial Dose'
    && Boolean(METHOD_CONFIG[method])
    && Boolean(findTargetForAdministrationMode(
      drugConfig,
      method,
      administrationMode,
      continuousTargetParameter,
    ))
  ));
};

export const getCalculationMethodConfigurationIssue = (
  drugConfig: any,
  administrationMode: AdministrationMode,
  continuousTargetParameter = 'Css',
): string | null => {
  if (!drugConfig) return null;
  if (resolveAvailableCalculationMethods(
    drugConfig,
    administrationMode,
    continuousTargetParameter,
  ).length > 0) {
    return null;
  }

  const modeLabel = getAdministrationModeLabel(administrationMode);
  const configuredMethods = Array.isArray(drugConfig?.calculation_methods)
    ? drugConfig.calculation_methods.filter((method: unknown) => (
      typeof method === 'string' && method !== 'Remedial Dose'
    ))
    : [];

  if (configuredMethods.length === 0) {
    return `后台尚未为${modeLabel}配置可用的 PK/PD 计算方法。`;
  }
  return `后台已配置计算方法，但尚未为${modeLabel}关联对应的 PK/PD 靶标。`;
};
